import {useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import styled from 'styled-components';
import aiIconUrl from '../../../assets/icons/ai.svg';
import {confidenceLabel} from '../utils/confidenceLabel';
import {formatDateShort} from '../utils/formatDate';
import {personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {useDismissSuggestedAction} from '../hooks/useDismissSuggestedAction';
import {useRegenerateAiSummary} from '../hooks/useRegenerateAiSummary';
import {AcceptSuggestionPanel} from './AcceptSuggestionPanel';
import type {AiSuggestedActionsList_ticket$key} from './__generated__/AiSuggestedActionsList_ticket.graphql';

const AiSuggestedActionsFragment = graphql`
	fragment AiSuggestedActionsList_ticket on Ticket {
		id
		status
		aiSummary {
			generatedAt
			isFinal
		}
		aiSuggestedActions {
			id
			title
			suggestedDueDate
			rationale
			confidence
			status
			assignee {
				id
				fullName
				initials
				profilePictureId
				active
				...ticketingPerson_person
			}
		}
	}
`;

const Section = styled.section`
	padding: 16px 20px;
	background: ${p => p.theme.ticketing.color.brand50};
	border: 1px solid ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.m};
`;
const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	margin-bottom: 12px;
`;
const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const RegenerateButton = styled.button`
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.interactivePrimary};
	background: transparent;
	border: 1px solid ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.m};
	padding: 4px 10px;
	cursor: pointer;
	&:disabled {
		opacity: 0.6;
		cursor: default;
	}
`;
const ErrorRow = styled.div`
	margin-bottom: 12px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.dangerText};
`;
const Icon = styled.img`
	width: 16px;
	height: 16px;
	color: ${p => p.theme.ticketing.color.interactivePrimary};
`;
const Title = styled.span`
	font: ${p => p.theme.ticketing.font.label1};
	color: ${p => p.theme.ticketing.color.interactivePrimary};
`;
const AiPill = styled.span`
	padding: 2px 8px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	background: ${p => p.theme.ticketing.color.brand100};
	color: ${p => p.theme.ticketing.color.interactivePrimary};
	border-radius: ${p => p.theme.ticketing.radius.full};
`;
const EmptyState = styled.p`
	margin: 0;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;
const List = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	> li + li {
		margin-top: 8px;
	}
`;
const Item = styled.li`
	padding: 12px;
	background: #ffffff;
	border: 1px solid ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.m};
`;
const ItemRow = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
`;
const ItemMain = styled.div`
	min-width: 0;
`;
const ItemTitle = styled.p`
	margin: 0;
	font: ${p => p.theme.ticketing.font.body2};
	font-weight: 500;
	color: ${p => p.theme.ticketing.color.dark900};
`;
const Meta = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 6px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;
const Assignee = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
`;
const Avatar = styled.span`
	display: inline-flex;
	height: 20px;
	width: 20px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: #e0e7ff;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #4338ca;
`;
const Rationale = styled.p`
	margin: 6px 0 0;
	max-height: 0;
	overflow: hidden;
	opacity: 0;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
	transition: opacity 0.15s ease, max-height 0.15s ease;
	${Item}:hover &, ${Item}:focus-within & {
		max-height: 8rem;
		opacity: 1;
	}
`;
const Actions = styled.div`
	display: flex;
	flex-shrink: 0;
	gap: 8px;
`;
const PrimaryButton = styled.button`
	padding: 4px 10px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #ffffff;
	background: ${p => p.theme.ticketing.color.interactivePrimary};
	border: 0;
	border-radius: ${p => p.theme.ticketing.radius.m};
	cursor: pointer;
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;
const SecondaryButton = styled.button`
	padding: 4px 10px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: ${p => p.theme.ticketing.color.interactivePrimary};
	background: transparent;
	border: 1px solid ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.m};
	cursor: pointer;
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

interface Props {
	ticketRef: AiSuggestedActionsList_ticket$key;
}

export function AiSuggestedActionsList({ticketRef}: Props) {
	const ticket = useFragment<AiSuggestedActionsList_ticket$key>(AiSuggestedActionsFragment, ticketRef);
	const {dismiss, inFlight} = useDismissSuggestedAction();
	const {regenerate, inFlight: regenerating, error: regenerateError} = useRegenerateAiSummary();
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const actions = ticket.aiSuggestedActions;
	const hasSummary = Boolean(ticket.aiSummary);
	const isTerminal = ticket.status === 'resolved' || ticket.status === 'closed';
	const hasActions = Boolean(actions && actions.length > 0);

	// AC #3: once the ticket is terminal, the action-items block hides entirely —
	// no suggestions, no empty state, no regenerate affordance.
	if (isTerminal) return null;

	// Render nothing when there is neither an action nor a summary to anchor the
	// block (the summary block alone covers the no-summary case).
	if (!hasActions && !hasSummary) return null;

	return (
		<Section>
			<Header>
				<HeaderLeft>
					<Icon src={aiIconUrl} alt="" role="img" />
					<Title>Recommended actions</Title>
					<AiPill>AI</AiPill>
				</HeaderLeft>
				<RegenerateButton type="button" disabled={regenerating} onClick={() => regenerate(ticket.id)}>
					{regenerating ? 'Regenerating…' : 'Regenerate'}
				</RegenerateButton>
			</Header>
			{regenerateError && <ErrorRow>{regenerateError.message}</ErrorRow>}
			{!hasActions ? (
				<EmptyState>No action items suggested — nothing actionable on this ticket right now.</EmptyState>
			) : (
				<List>
					{actions.map(action => {
						const label = confidenceLabel(action.confidence);
						const assigneeName = personDisplayName(action.assignee);
						return (
							<Item key={action.id}>
								<ItemRow>
									<ItemMain>
										<ItemTitle>{action.title}</ItemTitle>
										<Meta>
											{action.assignee && (
												<Assignee>
													<Avatar aria-hidden>{action.assignee.initials ?? '?'}</Avatar>
													<span>{assigneeName}</span>
												</Assignee>
											)}
											{action.suggestedDueDate && (
												<span>Due {formatDateShort(action.suggestedDueDate)}</span>
											)}
											{label && <span>{label}</span>}
										</Meta>
										{action.rationale && <Rationale>{action.rationale}</Rationale>}
									</ItemMain>
									<Actions>
										{/* AC #1/#3: "Add to ticket" toggles the inline accept panel (ACC-296). */}
										<PrimaryButton
											type="button"
											aria-label="Add to ticket"
											onClick={() => setExpandedId(expandedId === action.id ? null : action.id)}
										>
											Add to ticket
										</PrimaryButton>
										<SecondaryButton
											type="button"
											disabled={inFlight}
											onClick={() => dismiss(ticket.id, action.id)}
										>
											Dismiss
										</SecondaryButton>
									</Actions>
								</ItemRow>
								{expandedId === action.id && (
									<AcceptSuggestionPanel
										ticketId={ticket.id}
										action={{
											id: action.id,
											title: action.title,
											rationale: action.rationale ?? null,
											suggestedDueDate: action.suggestedDueDate ?? null,
											assignee: action.assignee
												? {
														id: action.assignee.id,
														fullName: action.assignee.fullName ?? null,
														initials: action.assignee.initials ?? null,
												  }
												: null,
										}}
										onCancel={() => setExpandedId(null)}
										onCompleted={() => setExpandedId(null)}
									/>
								)}
							</Item>
						);
					})}
				</List>
			)}
		</Section>
	);
}
